import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tooltip, Glyphicon, OverlayTrigger } from 'react-bootstrap';
import _ from 'underscore';
import { humanize, capitalize, camelize, titleize } from 'underscore.string';
import CargomaticCard from 'components/v2/card';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import ItemAlt from 'components/ui/tailwind/ItemAlt';
import OrderWebAPIUtils from 'utils/OrderWebAPIUtils';
import { withTranslation, Translation } from 'react-i18next';
import {
  weightUnitsOptions,
  temperatureUnitsOptions,
} from 'utils/localization/types';
import OrdersActionCreators from 'actions/OrdersActionCreators';

const hazmatClasses = [
  {
    localizationSlug: 'N_A',
    name: 'N/A',
    value: 'N/A',
  },
  {
    name: 1,
    value: 1,
  },
  {
    name: 2,
    value: 2,
  },
  {
    name: 3,
    value: 3,
  },
  {
    name: 4,
    value: 4,
  },
  {
    name: 5,
    value: 5,
  },
  {
    name: 6,
    value: 6,
  },
  {
    name: 7,
    value: 7,
  },
  {
    name: 8,
    value: 8,
  },
  {
    name: 9,
    value: 9,
  },
];

const hazmatPackagingGroup = [
  {
    localizationSlug: 'N_A',
    name: 'N/A',
    value: 'N/A',
  },
  {
    name: 'I',
    value: 'I',
  },
  {
    name: 'II',
    value: 'II',
  },
  {
    name: 'III',
    value: 'III',
  },
];

const serviceTypes = [
  {
    localizationSlug: 'LIVE_LOAD_UNLOAD',
    name: 'Live Load/Unload',
    value: 'live',
  },
  {
    localizationSlug: 'DROP_HOOK',
    name: 'Drop & Hook',
    value: 'drop',
  },
];

const movementTypes = [
  {
    localizationSlug: 'PICKUP',
    name: 'Pickup',
    value: 'pickup',
  },
  {
    localizationSlug: 'DELIVERY',
    name: 'Delivery',
    value: 'delivery',
  },
];

const orderTypes = [
  {
    localizationSlug: 'IMPORT',
    name: 'Import',
    value: 'import',
  },
  {
    localizationSlug: 'EXPORT',
    name: 'Export',
    value: 'export',
  },
];

const modeTypes = [
  {
    localizationSlug: 'DRAYAGE',
    name: 'Drayage',
    value: 'drayage',
  },
  {
    localizationSlug: 'INTERMODAL',
    name: 'Intermodal',
    value: 'intermodal',
  },
];

function formatDate(date, t) {
  return date ? moment(date).format('L') : t('NOT_SET');
}

const TOOLTIPS = {
  editModeDisabled: (
    <Tooltip id="edit-disabled-tooltip">
      <Translation>
        {(t, { i18n }) => <p>{t('EDIT_MODE_DISABLED_TIP')}</p>}
      </Translation>
    </Tooltip>
  ),
};

class InboundDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.retrieveOceanScacs();
  }

  async retrieveOceanScacs() {
    const ocean_scacs = await OrderWebAPIUtils.getCarrierScacs();
    if (ocean_scacs) {
      this.setState({
        ocean_scacs: ocean_scacs.OCEAN_SCAC_CODES.map((scac) => ({
          name: scac.carrier,
          value: scac.ocean_scac,
        })),
      });
    }
  }

  renderDropdownField(field, value, label, items, displayFunc, type) {
    const { t } = this.props;
    return (
      <DescriptionListItem label={t(label)} className="mb-3" dataTestId={field}>
        <ItemAlt
          name={field}
          type={type || 'dropdown'}
          menuItems={items}
          localizationKey="localizationSlug"
          displayValue={displayFunc}
          value={value}
          editable
          onValueSave={this.props.onEdit}
          glyphClass="float-left cursor-pointer mb-1"
        />
      </DescriptionListItem>
    );
  }

  renderDropdownWithInput(
    field,
    value,
    label,
    items,
    displayValue,
    renderConditionalField = true,
  ) {
    const { t } = this.props;
    return (
      <DescriptionListItem label={t(label)} className="mb-3" dataTestId={field}>
        <ItemAlt
          name={field}
          type="dropdown_with_input"
          menuItems={items}
          displayValue={displayValue}
          value={value}
          renderConditionalField={renderConditionalField}
          editable
          onValueSave={this.props.onEdit}
          glyphClass="float-left cursor-pointer mb-1"
        />
      </DescriptionListItem>
    );
  }

  renderField(field, type, label, formatter) {
    const { order, t } = this.props;
    let displayValue = null;
    if (field === 'reefer_temperature' && Number.isNaN(order[field])) {
      formatter = null;
    }
    if (type === 'date') {
      displayValue = formatDate(order[field], t);
    } else if (formatter) {
      displayValue = formatter(order[field]);
    }

    let heapClass = '';
    if (type && field && type === 'date') {
      heapClass = 'order-orderDetails-edit' + camelize(field) + ' heap';
    }

    let maxLength;
    if (field === 'special_instructions') maxLength = 500;

    return (
      <DescriptionListItem
        label={titleize(t(label, { count: 1 }))}
        className="mb-3"
        dataTestId={field}
      >
        <ItemAlt
          name={field}
          type={type}
          value={order[field]}
          displayValue={displayValue}
          editable
          onValueSave={this.props.onEdit}
          glyphClass="float-left cursor-pointer mb-1"
          saveButtonClass={`button button-green ${heapClass}`}
          maxLength={maxLength}
        />
      </DescriptionListItem>
    );
  }

  renderExportDateFields() {
    const {
      order: { order_type },
    } = this.props;

    if (order_type !== 'export') {
      return null;
    }
    return (
      <div>
        {this.renderField('last_free_date', 'date', 'LAST_CUT_DATE')}
        {this.renderField(
          'earliest_return_date',
          'date',
          'EARLIEST_RETURN_DATE',
        )}
        {this.renderField(
          'earliest_empty',
          'date',
          'EARLIEST_DATE_TO_GET_EMPTY',
        )}
      </div>
    );
  }

  renderImportDateFields() {
    const {
      order: { order_type },
    } = this.props;

    if (order_type !== 'import') {
      return null;
    }

    return (
      <>
        {this.renderField('last_free_date', 'date', 'LAST_FREE_DATE')}
        {this.renderField('arrival_date', 'date', 'ARRIVAL_DATE')}
        {this.renderField(
          'estimated_arrival',
          'date',
          'ESTIMATED_ARRIVAL_DATE',
        )}
      </>
    );
  }

  getCustomScacData(order) {
    const { ocean_scacs } = this.state;
    const { ocean_carrier_scac } = order;
    const scacKeys = (ocean_scacs || []).map((scac) => scac.name);
    const renderScacInput =
      (ocean_carrier_scac !== undefined || ocean_carrier_scac !== '') &&
      !scacKeys.includes(ocean_carrier_scac);
    const customScacInput = (ocean_scacs || []).find(
      (scac) => scac.value === ocean_carrier_scac,
    );

    return {
      renderScacInput,
      customScacValue: !_.isEmpty(customScacInput) ? customScacInput.value : '',
      customScacInput:
        !_.isEmpty(customScacInput) && customScacInput.name !== 'Custom'
          ? customScacInput.name
          : ocean_carrier_scac,
    };
  }

  render() {
    const order = this.props.order;
    const originating =
      order?.originating && order?.originating.length > 0
        ? order?.originating[0].address
        : '';

    const modeDisabled =
      order?.shipments.length > 0 &&
      order?.shipments.some((shipment) => shipment.shipment_type === 'rail');

    const scacData = this.getCustomScacData(order);
    const { t } = this.props;
    return (
      <CargomaticCard
        header={() => <h3 className="my-0">{t('ORDER_DETAILS')}</h3>}
      >
        <div className="grid grid-cols-2 grid-gap-6">
          <DescriptionListItem
            label={t('ORDER_TYPE')}
            className="mb-3"
            dataTestId="order-type-kv"
          >
            <ItemAlt
              name="order_type"
              type="dropdown"
              menuItems={orderTypes}
              localizationKey="localizationSlug"
              displayValue={t(order?.order_type?.toUpperCase())}
              value={order?.order_type}
              editable={false}
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          <DescriptionListItem
            label={titleize(t('MODE'))}
            className="mb-3"
            dataTestId="order-mode-kv"
          >
            {modeDisabled && (
              <div className="">
                <span className="mr-2">{capitalize(order?.mode || '')}</span>
                <OverlayTrigger
                  placement="top"
                  overlay={TOOLTIPS.editModeDisabled}
                  style={{ display: 'inline-block' }}
                >
                  <Glyphicon glyph="info-sign" />
                </OverlayTrigger>
              </div>
            )}
            {!modeDisabled && (
              <ItemAlt
                name="mode"
                type="dropdown"
                menuItems={modeTypes}
                localizationKey="localizationSlug"
                displayValue={t(order?.mode?.toUpperCase())}
                value={order?.mode}
                editable
                onValueSave={this.props.onEdit}
                glyphClass="float-left cursor-pointer mb-1"
              />
            )}
          </DescriptionListItem>
          <DescriptionListItem
            label={t('SERVICE_TYPE')}
            className="mb-3"
            dataTestId="order-service-type-kv"
          >
            <ItemAlt
              name="service_type"
              type="dropdown"
              localizationKey="localizationSlug"
              menuItems={serviceTypes}
              displayValue={t(
                _.findWhere(serviceTypes, {
                  value: order?.service_type,
                })?.localizationSlug,
              )}
              value={order?.service_type}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          <DescriptionListItem
            label={t('MOVEMENT_TYPE')}
            className="mb-3"
            dataTestId="order-movement-type-kv"
          >
            <ItemAlt
              name="movement_type"
              type="dropdown"
              localizationKey="localizationSlug"
              menuItems={movementTypes}
              displayValue={humanize(order?.movement_type)}
              value={order?.movement_type}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          {this.renderField('total_weight', 'number', 'TOTAL_WEIGHT')}
          {this.renderField('certified_weight', 'number', 'CERTIFIED_WEIGHT')}
          <DescriptionListItem
            label={t('WEIGHT_UNIT')}
            className="mb-3"
            dataTestId="order-weight-unit-kv"
          >
            <ItemAlt
              name="weight_units"
              type="dropdown"
              localizationKey="localizationSlug"
              menuItems={weightUnitsOptions}
              displayValue={order?.weight_units}
              value={order?.weight_units}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          {this.renderField('count', 'number', 'COUNT')}
          {this.renderField(
            'reefer_temperature',
            'string',
            'REEFER_TEMPERATURE',
          )}
          <DescriptionListItem
            label={t('REEFER_TEMPERATURE_UNIT')}
            className="mb-3"
            dataTestId="reefer-temperature-kv"
          >
            <ItemAlt
              name="reefer_temperature_unit"
              type="dropdown"
              localizationKey="localizationSlug"
              disableTitleize
              menuItems={temperatureUnitsOptions}
              displayValue={t(
                _.findWhere(temperatureUnitsOptions, {
                  value: order?.reefer_temperature_unit,
                })?.localizationSlug,
              )}
              value={order?.reefer_temperature_unit}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          {this.renderField(
            'beneficial_cargo_owner',
            'string',
            'BENEFICIAL_CARGO_OWNER',
          )}
          <DescriptionListItem
            label={t('ORIGINATING')}
            className="mb-3"
            dataTestId="order-originating-kv"
          >
            <ItemAlt
              name="originating"
              type="address"
              value={originating || ''}
              editable
              onValueSave={(loc) => {
                const update = {
                  address: loc.originating,
                };
                OrdersActionCreators.updateOrder(order?._id, {
                  originating: update,
                });
              }}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          <div>
            {this.state.ocean_scacs &&
              this.state.ocean_scacs.length &&
              this.renderDropdownWithInput(
                'ocean_carrier_scac',
                scacData.customScacValue,
                'STEAMSHIP_RAILROAD',
                this.state.ocean_scacs,
                scacData.customScacInput,
                scacData.renderScacInput,
              )}
          </div>
          {this.renderField('vessel_or_rail', 'string', 'VESSEL')}
          {this.renderField('train', 'string', 'TRAIN')}
          {this.renderImportDateFields()}
          {this.renderExportDateFields()}
          {this.renderField(
            'special_instructions',
            'string',
            'SPECIAL_INSTRUCTIONS',
          )}
          {this.renderField('cargo_description', 'string', 'CARGO_DESCRIPTION')}
          {this.renderDropdownField(
            'hazmat_class',
            order?.hazmat_class,
            'HAZMAT_CLASS',
            hazmatClasses,
          )}
          {this.renderDropdownField(
            'hazmat_packaging_group',
            order?.hazmat_packaging_group,
            'HAZMAT_PACKAGING_GROUP',
            hazmatPackagingGroup,
          )}
          {this.renderField(
            'hazmat_description',
            'string',
            'HAZMAT_DESCRIPTION',
          )}
        </div>
      </CargomaticCard>
    );
  }
}

InboundDetails.propTypes = {
  order: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default withTranslation()(InboundDetails);
